import React, { useState, useEffect } from 'react';
import { authApi, ChatHistory, CustomerInquiry } from '../services/authApi';
import { useMediaQuery } from '../hooks/useMediaQuery';

interface ChatHistoryViewerProps {
  token: string;
  onLogout: () => void;
}

interface SessionData {
  sessionId: string;
  customerInfo?: CustomerInquiry;
  chatHistory: ChatHistory[];
  firstMessageDate: string;
  lastMessageDate: string;
  messageCount: number;
}

const ChatHistoryViewer: React.FC<ChatHistoryViewerProps> = ({ token, onLogout }) => {
  const [chatHistory, setChatHistory] = useState<ChatHistory[]>([]);
  const [customerInquiries, setCustomerInquiries] = useState<CustomerInquiry[]>([]);
  const [sessionData, setSessionData] = useState<SessionData[]>([]);
  const [expandedSessions, setExpandedSessions] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [vectorGenerating, setVectorGenerating] = useState(false);
  const [vectorMessage, setVectorMessage] = useState('');
  const [scrapingRunning, setScrapingRunning] = useState(false);
  const [scrapingMessage, setScrapingMessage] = useState('');
  const [newPropertiesCount, setNewPropertiesCount] = useState<number | null>(null);
  const [newPropertiesList, setNewPropertiesList] = useState<string[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const isMobile = useMediaQuery('(max-width: 768px)');

  useEffect(() => {
    fetchChatHistory();
    fetchCustomerInquiries();
    checkScrapingStatus();
  }, []);

  useEffect(() => {
    if (chatHistory.length > 0 || customerInquiries.length > 0) {
      groupBySession();
    }
  }, [chatHistory, customerInquiries]);

  const checkScrapingStatus = async () => {
    try {
      const status = await authApi.getScrapingStatus(token);
      setScrapingRunning(status.isRunning);
      if (status.isRunning) {
        setScrapingMessage(`⏳ ${status.status}`);
      }
    } catch (err) {
      // エラーは無視
    }
  };

  const fetchChatHistory = async () => {
    try {
      setLoading(true);
      const history = await authApi.getChatHistory(token);
      setChatHistory(Array.isArray(history) ? history : []);
    } catch (err: any) {
      setError('会話履歴の取得に失敗しました');
      setChatHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerInquiries = async () => {
    try {
      const inquiries = await authApi.getCustomerInquiries(token);
      setCustomerInquiries(Array.isArray(inquiries) ? inquiries : []);
    } catch (err: any) {
      console.error('顧客情報の取得に失敗しました');
      setCustomerInquiries([]);
    }
  };

  const groupBySession = () => {
    const sessionMap = new Map<string, SessionData>();

    chatHistory.forEach(chat => {
      if (!sessionMap.has(chat.sessionId)) {
        sessionMap.set(chat.sessionId, {
          sessionId: chat.sessionId,
          chatHistory: [],
          firstMessageDate: chat.createdAt,
          lastMessageDate: chat.createdAt,
          messageCount: 0
        });
      }
      const session = sessionMap.get(chat.sessionId)!;
      session.chatHistory.push(chat);
      session.messageCount++;
      if (new Date(chat.createdAt) < new Date(session.firstMessageDate)) {
        session.firstMessageDate = chat.createdAt;
      }
      if (new Date(chat.createdAt) > new Date(session.lastMessageDate)) {
        session.lastMessageDate = chat.createdAt;
      }
    });

    customerInquiries.forEach(inquiry => {
      if (sessionMap.has(inquiry.sessionId)) {
        sessionMap.get(inquiry.sessionId)!.customerInfo = inquiry;
      } else {
        sessionMap.set(inquiry.sessionId, {
          sessionId: inquiry.sessionId,
          customerInfo: inquiry,
          chatHistory: [],
          firstMessageDate: inquiry.createdAt,
          lastMessageDate: inquiry.createdAt,
          messageCount: 0
        });
      }
    });

    const sorted = Array.from(sessionMap.values()).sort((a, b) => 
      new Date(b.lastMessageDate).getTime() - new Date(a.lastMessageDate).getTime()
    );
    setSessionData(sorted);
  };

  const toggleSession = (sessionId: string) => {
    const newExpanded = new Set(expandedSessions);
    if (newExpanded.has(sessionId)) {
      newExpanded.delete(sessionId);
    } else {
      newExpanded.add(sessionId);
    }
    setExpandedSessions(newExpanded);
  };

  const handleLogout = async () => {
    try {
      await authApi.logout(token);
    } catch (err) {
      // ログアウトエラーは無視
    } finally {
      localStorage.removeItem('adminToken');
      onLogout();
    }
  };

  const handleGenerateEmbeddings = async () => {
    if (!window.confirm('全物件のベクトルEmbeddingを生成します。\n処理に時間がかかる場合があります。\n実行しますか？')) {
      return;
    }

    try {
      setVectorGenerating(true);
      setVectorMessage('');
      const result = await authApi.generateAllEmbeddings(token);
      setVectorMessage(`✅ ${result.message}`);
    } catch (err: any) {
      setVectorMessage(`❌ エラー: ${err.response?.data?.error || err.message}`);
    } finally {
      setVectorGenerating(false);
    }
  };

  const handleStartScraping = async () => {
    if (!window.confirm('物件情報のスクレイピングを開始します。\n処理に時間がかかる場合があります。\n実行しますか？')) {
      return;
    }

    try {
      setScrapingMessage('');
      const result = await authApi.startScraping(token);
      setScrapingMessage(`✅ ${result.message}`);
      setScrapingRunning(true);
      
      const interval = setInterval(async () => {
        const status = await authApi.getScrapingStatus(token);
        setScrapingRunning(status.isRunning);
        setScrapingMessage(`⏳ ${status.status}`);
        if (!status.isRunning) {
          clearInterval(interval);
          setScrapingMessage(`✅ ${status.status}`);
        }
      }, 3000);
    } catch (err: any) {
      setScrapingMessage(`❌ エラー: ${err.response?.data?.message || err.message}`);
    }
  };

  const handleStopScraping = async () => {
    try {
      const result = await authApi.stopScraping(token);
      setScrapingMessage(`⚠️ ${result.message}`);
    } catch (err: any) {
      setScrapingMessage(`❌ エラー: ${err.response?.data?.message || err.message}`);
    }
  };

  const handleCheckNewProperties = async () => {
    try {
      setScrapingMessage('🔍 新規物件をチェック中...');
      const result = await authApi.checkNewProperties(token);
      const count = result.count || 0;
      const properties = result.newProperties || [];
      setNewPropertiesCount(count);
      setNewPropertiesList(properties);
      if (count === 0) {
        setScrapingMessage('✅ 新規物件はありません');
      } else {
        setScrapingMessage(`✅ ${count}件の新規物件が見つかりました`);
      }
    } catch (err: any) {
      setScrapingMessage(`❌ エラー: ${err.response?.data?.message || err.message}`);
      setNewPropertiesCount(0);
      setNewPropertiesList([]);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ja-JP', {
      timeZone: 'Asia/Tokyo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  if (loading) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '50px',
        fontSize: '16px',
        color: '#666'
      }}>
        📊 会話履歴を読み込み中...
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      padding: isMobile ? '15px' : '20px'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? '15px' : '0'
      }}>
        <h1 style={{
          margin: 0,
          color: '#2c3e50',
          fontSize: isMobile ? '24px' : '28px'
        }}>
          📊 管理ダッシュボード
        </h1>
        <div style={{
          display: 'flex',
          gap: '10px',
          flexDirection: isMobile ? 'column' : 'row',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={handleCheckNewProperties}
            style={{
              padding: '10px 20px',
              backgroundColor: '#17a2b8',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            🔍 新規物件チェック
          </button>
          <button
            onClick={handleStartScraping}
            disabled={scrapingRunning}
            style={{
              padding: '10px 20px',
              backgroundColor: scrapingRunning ? '#6c757d' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: scrapingRunning ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              opacity: scrapingRunning ? 0.6 : 1
            }}
          >
            {scrapingRunning ? '⏳ 実行中...' : '🕷️ スクレイピング開始'}
          </button>
          {scrapingRunning && (
            <button
              onClick={handleStopScraping}
              style={{
                padding: '10px 20px',
                backgroundColor: '#ffc107',
                color: '#000',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              ⏸️ 停止
            </button>
          )}
          <button
            onClick={handleGenerateEmbeddings}
            disabled={vectorGenerating}
            style={{
              padding: '10px 20px',
              backgroundColor: vectorGenerating ? '#6c757d' : '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: vectorGenerating ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              opacity: vectorGenerating ? 0.6 : 1
            }}
          >
            {vectorGenerating ? '⏳ 生成中...' : '🔄 ベクトル生成'}
          </button>
          <button
            onClick={handleLogout}
            style={{
              padding: '10px 20px',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            ログアウト
          </button>
        </div>
      </div>

      {vectorMessage && (
        <div style={{
          backgroundColor: vectorMessage.startsWith('✅') ? '#d4edda' : '#f8d7da',
          color: vectorMessage.startsWith('✅') ? '#155724' : '#721c24',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '20px',
          fontSize: '14px'
        }}>
          {vectorMessage}
        </div>
      )}

      {scrapingMessage && (
        <div style={{
          backgroundColor: scrapingMessage.startsWith('✅') ? '#d4edda' : 
                         scrapingMessage.startsWith('⏳') ? '#d1ecf1' : 
                         scrapingMessage.startsWith('⚠️') ? '#fff3cd' : '#f8d7da',
          color: scrapingMessage.startsWith('✅') ? '#155724' : 
                 scrapingMessage.startsWith('⏳') ? '#0c5460' : 
                 scrapingMessage.startsWith('⚠️') ? '#856404' : '#721c24',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '20px',
          fontSize: '14px'
        }}>
          {scrapingMessage}
        </div>
      )}

      {newPropertiesCount !== null && newPropertiesCount > 0 && (
        <div style={{
          backgroundColor: '#fff3cd',
          color: '#856404',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '20px',
          fontSize: '14px'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '10px' }}>
            📋 新規物件リスト ({newPropertiesCount}件)
          </div>
          <ul style={{ margin: 0, paddingLeft: '20px' }}>
            {newPropertiesList.map((name, index) => (
              <li key={index}>{name}</li>
            ))}
          </ul>
        </div>
      )}

      {error && (
        <div style={{
          backgroundColor: '#f8d7da',
          color: '#721c24',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          {error}
        </div>
      )}

      {selectedSessionId ? (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: isMobile ? '15px' : '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: '1px solid #ddd'
        }}>
          <button
            onClick={() => setSelectedSessionId(null)}
            style={{
              padding: '8px 16px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              marginBottom: '20px'
            }}
          >
            ← 一覧に戻る
          </button>
          <h2 style={{ marginBottom: '20px', color: '#2c3e50' }}>
            セッション: {selectedSessionId}
          </h2>
          {chatHistory.filter(c => c.sessionId === selectedSessionId).map((chat) => (
            <div
              key={chat.id}
              style={{
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                padding: isMobile ? '15px' : '20px',
                backgroundColor: '#f8f9fa',
                marginBottom: '15px'
              }}
            >
              <div style={{
                fontSize: '12px',
                color: '#666',
                marginBottom: '15px'
              }}>
                {formatDate(chat.createdAt)}
              </div>
              <div style={{ marginBottom: '15px' }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: 'bold',
                  color: '#1976d2',
                  marginBottom: '8px'
                }}>
                  👤 ユーザー:
                </div>
                <div style={{
                  backgroundColor: 'white',
                  padding: '12px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  lineHeight: '1.5'
                }}>
                  {chat.userMessage}
                </div>
              </div>
              <div>
                <div style={{
                  fontSize: '14px',
                  fontWeight: 'bold',
                  color: '#28a745',
                  marginBottom: '8px'
                }}>
                  🤖 AI応答:
                </div>
                <div style={{
                  backgroundColor: 'white',
                  padding: '12px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  whiteSpace: 'pre-wrap'
                }}>
                  {chat.botResponse}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: isMobile ? '15px' : '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: '1px solid #ddd'
        }}>
          <div style={{
            marginBottom: '20px',
            fontSize: '16px',
            color: '#666'
          }}>
            総セッション数: {sessionData.length}件 | 総会話数: {chatHistory.length}件
          </div>

          {sessionData.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: '#666'
            }}>
              データがありません
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gap: '20px'
            }}>
              {sessionData.map((session) => (
                <div
                  key={session.sessionId}
                  style={{
                    border: '2px solid #e0e0e0',
                    borderRadius: '12px',
                    padding: isMobile ? '15px' : '20px',
                    backgroundColor: '#f8f9fa'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '15px',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? '10px' : '0'
                  }}>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: 'bold',
                      color: '#2c3e50'
                    }}>
                      📋 セッション: {session.sessionId}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: '#666',
                      backgroundColor: '#e9ecef',
                      padding: '4px 12px',
                      borderRadius: '20px'
                    }}>
                      💬 {session.messageCount}件の会話
                    </div>
                  </div>

                  {session.customerInfo && (
                    <div style={{
                      backgroundColor: 'white',
                      padding: '15px',
                      borderRadius: '8px',
                      marginBottom: '15px',
                      border: '1px solid #dee2e6'
                    }}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: 'bold',
                        color: '#1976d2',
                        marginBottom: '12px'
                      }}>
                        👤 顧客情報
                      </div>
                      <div style={{ display: 'grid', gap: '8px', fontSize: '14px' }}>
                        {session.customerInfo.name && (
                          <div>
                            <strong>名前:</strong> {session.customerInfo.name}
                          </div>
                        )}
                        {session.customerInfo.phone && (
                          <div>
                            <strong>📞 電話:</strong> {session.customerInfo.phone}
                          </div>
                        )}
                        {session.customerInfo.email && (
                          <div>
                            <strong>✉️ メール:</strong> {session.customerInfo.email}
                          </div>
                        )}
                        <div>
                          <strong>📝 問い合わせ種別:</strong> {session.customerInfo.inquiryType}
                        </div>
                      </div>
                    </div>
                  )}

                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: '12px',
                    color: '#666',
                    marginBottom: '15px'
                  }}>
                    <div>
                      <strong>初回:</strong> {formatDate(session.firstMessageDate)}
                    </div>
                    <div>
                      <strong>最終:</strong> {formatDate(session.lastMessageDate)}
                    </div>
                  </div>

                  {session.messageCount > 0 && (
                    <div>
                      <button
                        onClick={() => toggleSession(session.sessionId)}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: '#1976d2',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          marginRight: '10px',
                          marginBottom: isMobile ? '10px' : '0'
                        }}
                      >
                        {expandedSessions.has(session.sessionId) ? '▼ 会話を閉じる' : '▶ 会話を表示'}
                      </button>
                      <button
                        onClick={() => setSelectedSessionId(session.sessionId)}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: '#28a745',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}
                      >
                        📜 全会話履歴を見る ({session.messageCount}件)
                      </button>
                    </div>
                  )}

                  {expandedSessions.has(session.sessionId) && session.chatHistory.length > 0 && (
                    <div style={{
                      marginTop: '15px',
                      paddingTop: '15px',
                      borderTop: '1px solid #dee2e6'
                    }}>
                      {session.chatHistory.slice(0, 3).map((chat) => (
                        <div
                          key={chat.id}
                          style={{
                            backgroundColor: 'white',
                            padding: '12px',
                            borderRadius: '8px',
                            marginBottom: '10px',
                            border: '1px solid #e0e0e0'
                          }}
                        >
                          <div style={{
                            fontSize: '11px',
                            color: '#999',
                            marginBottom: '8px'
                          }}>
                            {formatDate(chat.createdAt)}
                          </div>
                          <div style={{ marginBottom: '10px' }}>
                            <div style={{
                              fontSize: '13px',
                              fontWeight: 'bold',
                              color: '#1976d2',
                              marginBottom: '5px'
                            }}>
                              👤 ユーザー:
                            </div>
                            <div style={{
                              fontSize: '13px',
                              lineHeight: '1.4'
                            }}>
                              {chat.userMessage}
                            </div>
                          </div>
                          <div>
                            <div style={{
                              fontSize: '13px',
                              fontWeight: 'bold',
                              color: '#28a745',
                              marginBottom: '5px'
                            }}>
                              🤖 AI応答:
                            </div>
                            <div style={{
                              fontSize: '13px',
                              lineHeight: '1.4',
                              whiteSpace: 'pre-wrap'
                            }}>
                              {chat.botResponse.length > 200 ? chat.botResponse.substring(0, 200) + '...' : chat.botResponse}
                            </div>
                          </div>
                        </div>
                      ))}
                      {session.chatHistory.length > 3 && (
                        <div style={{
                          textAlign: 'center',
                          color: '#666',
                          fontSize: '13px',
                          marginTop: '10px'
                        }}>
                          他 {session.chatHistory.length - 3}件の会話があります
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ChatHistoryViewer;